/**
 * Route registry — single source of truth for the admin's lazy-loaded
 * page chunks. Two consumers:
 *
 *   1. `App.tsx` resolves a `lazy()` component from each loader.
 *   2. `AppShell` calls `prefetchRoute(path)` on sidebar link hover so the
 *      chunk download starts BEFORE the click. By the time the user
 *      releases the mouse the chunk is usually already cached, which
 *      removes the 200-500 ms first-visit lag you feel today.
 *
 * Phase 2 (2026-06-09): all module pages now live under their module
 * folder (`cms/`, `careers/`, `crm/`, `administration/`). Old top-level
 * URLs (/posts, /users, etc.) keep working via `LEGACY_REDIRECTS`.
 *
 * Add new routes here, NEVER inline a `lazy()` in App.tsx again.
 */

// Loose component type — some pages take props (e.g. LeadsListPage's
// `formType` for the Newsletter view). Keeping the loader generic
// avoids forcing every page into a no-prop signature.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ChunkLoader = () => Promise<{ default: React.ComponentType<any> }>;

export const routeLoaders: Record<string, ChunkLoader> = {
  // Auth
  '/login':                          () => import('@/app/routes/_auth/LoginPage'),

  // Home — module selector + auto-redirect for single-module users.
  '/':                               () => import('@/app/routes/_protected/HomePage'),

  // Module dashboards — each one a hub with KPI strip + sub-area links.
  '/cms':                            () => import('@/app/routes/_protected/cms/CmsDashboardPage'),
  '/careers':                        () => import('@/app/routes/_protected/careers/CareersDashboardPage'),
  '/crm':                            () => import('@/app/routes/_protected/crm/CrmDashboardPage'),
  '/administration':                 () => import('@/app/routes/_protected/administration/AdministrationDashboardPage'),

  // CMS — all sub-areas now nested under /cms.
  '/cms/posts':                      () => import('@/app/routes/_protected/cms/posts/PostsListPage'),
  '/cms/posts/new':                  () => import('@/app/routes/_protected/cms/posts/new/PostNewPage'),
  '/cms/posts/:id/edit':             () => import('@/app/routes/_protected/cms/posts/edit/PostEditPage'),
  '/cms/categories':                 () => import('@/app/routes/_protected/cms/categories/CategoriesPage'),
  '/cms/tags':                       () => import('@/app/routes/_protected/cms/tags/TagsPage'),
  '/cms/media':                      () => import('@/app/routes/_protected/cms/media/MediaPage'),
  '/cms/insights':                   () => import('@/app/routes/_protected/cms/insights/InsightsPage'),
  '/cms/popups':                     () => import('@/app/routes/_protected/cms/PopupsListPage'),
  '/cms/team':                       () => import('@/app/routes/_protected/cms/TeamListPage'),
  '/cms/seo':                        () => import('@/app/routes/_protected/cms/SeoPage'),

  // Careers (unchanged)
  '/careers/jobs':                   () => import('@/app/routes/_protected/careers/JobsListPage'),
  '/careers/jobs/new':               () => import('@/app/routes/_protected/careers/JobNewPage'),
  '/careers/jobs/:id/edit':          () => import('@/app/routes/_protected/careers/JobEditPage'),
  '/careers/applications':           () => import('@/app/routes/_protected/careers/ApplicationsInboxPage'),

  // CRM (unchanged)
  '/crm/leads':                      () => import('@/app/routes/_protected/crm/LeadsListPage'),
  '/crm/leads/:id':                  () => import('@/app/routes/_protected/crm/LeadDetailPage'),
  '/crm/newsletter':                 () => import('@/app/routes/_protected/crm/LeadsListPage'),
  '/crm/newsletter/:id':             () => import('@/app/routes/_protected/crm/LeadDetailPage'),

  // Administration — all sub-areas now nested under /administration.
  '/administration/users':           () => import('@/app/routes/_protected/administration/users/UsersPage'),
  '/administration/users/:id':       () => import('@/app/routes/_protected/administration/users/UserDetailPage'),
  '/administration/teams':           () => import('@/app/routes/_protected/administration/teams/TeamsListPage'),
  '/administration/teams/:id':       () => import('@/app/routes/_protected/administration/teams/TeamDetailPage'),
  '/administration/roles':           () => import('@/app/routes/_protected/administration/roles/RolesListPage'),
  '/administration/roles/:id':       () => import('@/app/routes/_protected/administration/roles/RoleEditPage'),
  '/administration/access-control':  () => import('@/app/routes/_protected/administration/access/AccessControlPage'),
  '/administration/audit':           () => import('@/app/routes/_protected/administration/audit/AuditLogPage'),
  '/administration/settings':        () => import('@/app/routes/_protected/administration/settings/SettingsPage'),
};

/**
 * Old top-level URLs map to their new module-scoped paths. App.tsx
 * uses this to render a `<Navigate replace />` for each old URL so
 * existing bookmarks, audit-log entries, and email links keep working
 * forever. Sub-paths (e.g. /posts/abc/edit) are handled by wildcard
 * redirects in App.tsx, not enumerated here.
 */
export const LEGACY_REDIRECTS: Record<string, string> = {
  '/posts':                 '/cms/posts',
  '/categories':            '/cms/categories',
  '/tags':                  '/cms/tags',
  '/media':                 '/cms/media',
  '/insights':              '/cms/insights',
  '/users':                 '/administration/users',
  '/teams':                 '/administration/teams',
  '/roles':                 '/administration/roles',
  '/access-control':        '/administration/access-control',
  '/audit':                 '/administration/audit',
  '/settings':              '/administration/settings',
};

/**
 * Trigger a chunk download for the given path. Safe to call multiple
 * times — Vite dedupes the dynamic import internally. Silently no-ops
 * for unknown paths (sub-routes with dynamic params we don't index).
 *
 * Designed for `onMouseEnter` / `onFocus` on sidebar links.
 */
export function prefetchRoute(path: string): void {
  const loader = routeLoaders[path];
  if (!loader) return;
  // Fire-and-forget; we don't care about the result here. Vite caches
  // the resolved promise so the eventual `lazy()` resolves instantly.
  loader().catch(() => {
    /* Network blip during prefetch — the real navigation will retry. */
  });
}
